// Atribución de campañas: ID de visitante propio y toques de campaña.
//
// El ID de visitante (gvid) y los toques se guardan en cookies propias del
// sitio y se replican en localStorage. Las cookies sobreviven a los
// localStorage.clear() que hace la app al reiniciar el flujo, y localStorage
// sirve de respaldo si el navegador bloquea las cookies.

const VISITOR_KEY = 'glv_id';
const FIRST_TOUCH_KEY = 'glv_ft';
const LAST_TOUCH_KEY = 'glv_lt';

const VISITOR_MAX_AGE_DAYS = 400;
const TOUCH_MAX_AGE_DAYS = 90;
// Una recarga o la misma URL dentro de este tiempo no cuenta como toque nuevo.
const SAME_TOUCH_WINDOW_MS = 30 * 60 * 1000;
const MAX_VALUE_LENGTH = 200;

export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
// Mismo orden de prioridad que resolveUtmReferral para elegir el clickId.
export const CLICK_ID_PARAMS = ['gclid', 'fbclid', 'ttclid', 'v', 'click_id', 'msclkid'];
// fbclid no cuenta como pagado: Meta lo agrega también a los clics orgánicos.
const PAID_CLICK_ID_PARAMS = ['gclid', 'ttclid', 'v', 'click_id', 'msclkid'];

function clean(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, MAX_VALUE_LENGTH) : null;
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const cookie = document.cookie.split('; ').find((value) => value.startsWith(prefix));
  if (!cookie) return null;
  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch (error) {
    return null;
  }
}

// Cookie solo del host actual (sin domain): sandbox y producción no se mezclan.
function writeCookie(name, value, maxAgeDays) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeDays * 86400}; SameSite=Lax${secure}`;
}

function readLocal(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

// Lee la cookie y, si falta, localStorage. Si una de las dos copias se perdió,
// la restaura desde la otra.
function readStored(key, maxAgeDays) {
  const fromCookie = readCookie(key);
  const fromLocal = readLocal(key);
  const value = fromCookie || fromLocal;
  if (value && (!fromCookie || !fromLocal)) writeStored(key, value, maxAgeDays);
  return value;
}

function removeStored(key) {
  try {
    writeCookie(key, '', 0);
  } catch (error) {
    // Cookies bloqueadas.
  }
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // localStorage no disponible.
  }
}

function writeStored(key, value, maxAgeDays) {
  try {
    writeCookie(key, value, maxAgeDays);
  } catch (error) {
    // Cookies bloqueadas: queda la copia en localStorage.
  }
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // localStorage no disponible (modo privado antiguo, cuota llena).
  }
}

function readTouch(key) {
  const raw = readStored(key, TOUCH_MAX_AGE_DAYS);
  if (!raw) return null;
  try {
    const touch = JSON.parse(raw);
    return touch && typeof touch === 'object' ? touch : null;
  } catch (error) {
    return null;
  }
}

function writeTouch(key, touch) {
  writeStored(key, JSON.stringify(touch), TOUCH_MAX_AGE_DAYS);
}

function isExpired(touch, now) {
  const capturedAt = new Date(touch?.capturedAt).getTime();
  if (Number.isNaN(capturedAt)) return true;
  return now.getTime() - capturedAt > TOUCH_MAX_AGE_DAYS * 86400 * 1000;
}

function generateVisitorId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Respaldo para navegadores sin randomUUID (RFC 4122 v4).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

/** Devuelve el gvid del visitante y lo crea en la primera visita. */
export function getOrCreateVisitorId() {
  let visitorId = clean(readStored(VISITOR_KEY, VISITOR_MAX_AGE_DAYS));
  if (!visitorId || visitorId.length > 50) visitorId = generateVisitorId();
  // Se reescribe siempre para renovar la vigencia de la cookie.
  writeStored(VISITOR_KEY, visitorId, VISITOR_MAX_AGE_DAYS);
  return visitorId;
}

/**
 * Borra el último toque cuando ya se usó para atribuir un crédito. El ID de
 * visitante y el primer toque se conservan para el análisis.
 */
export function clearLastTouch() {
  removeStored(LAST_TOUCH_KEY);
}

function externalReferrerDomain(referrer, hostname) {
  if (!referrer) return null;
  try {
    const referrerHost = new URL(referrer).hostname;
    return referrerHost && referrerHost !== hostname ? referrerHost : null;
  } catch (error) {
    return null;
  }
}

/** Arma un toque de campaña con los parámetros de la URL, o null si no hay. */
export function readTouchFromUrl({ search = '', pathname = '/', referrer = '', hostname = '', now = new Date() } = {}) {
  const params = new URLSearchParams(search);

  const utm = {};
  UTM_PARAMS.forEach((key) => {
    const value = clean(params.get(key));
    if (value) utm[key.replace('utm_', '')] = value;
  });

  const clickIds = {};
  CLICK_ID_PARAMS.forEach((key) => {
    const value = clean(params.get(key));
    if (value) clickIds[key] = value;
  });

  const utmId = clean(params.get('utm_id'));
  const partnerId = clean(params.get('partnerId'));

  if (!utmId && !partnerId && Object.keys(utm).length === 0 && Object.keys(clickIds).length === 0) {
    return null;
  }

  const clickId = CLICK_ID_PARAMS.map((key) => clickIds[key]).find(Boolean) || null;
  const paid = Boolean(partnerId || utmId || PAID_CLICK_ID_PARAMS.some((key) => clickIds[key]));

  return {
    capturedAt: now.toISOString(),
    landingPath: clean(pathname) || '/',
    referrerDomain: externalReferrerDomain(referrer, hostname),
    utm,
    utmId,
    partnerId,
    typeCustomer: clean(params.get('typeCustomer')),
    clickIds,
    clickId,
    paid,
  };
}

function touchSignature(touch) {
  return JSON.stringify([touch.utm, touch.utmId, touch.partnerId, touch.clickIds]);
}

function isSameRecentTouch(previous, touch, now) {
  if (!previous || touchSignature(previous) !== touchSignature(touch)) return false;
  return now.getTime() - new Date(previous.capturedAt).getTime() < SAME_TOUCH_WINDOW_MS;
}

/**
 * Registra el toque de la URL actual.
 * - Primer toque: el primero de los últimos 90 días; no se sobrescribe.
 * - Último toque: un toque pagado reemplaza a cualquiera; uno no pagado solo
 *   reemplaza a otro no pagado. Una visita sin parámetros no cambia nada.
 */
export function captureAttribution(location = window.location, { referrer = typeof document !== 'undefined' ? document.referrer : '', now = new Date() } = {}) {
  const visitorId = getOrCreateVisitorId();
  let firstTouch = readTouch(FIRST_TOUCH_KEY);
  let lastTouch = readTouch(LAST_TOUCH_KEY);
  if (firstTouch && isExpired(firstTouch, now)) firstTouch = null;
  if (lastTouch && isExpired(lastTouch, now)) lastTouch = null;

  const touch = readTouchFromUrl({
    search: location.search,
    pathname: location.pathname,
    hostname: location.hostname,
    referrer,
    now,
  });

  if (!touch || isSameRecentTouch(lastTouch, touch, now)) {
    return { visitorId, firstTouch, lastTouch, newTouch: null };
  }

  if (!firstTouch) {
    firstTouch = touch;
    writeTouch(FIRST_TOUCH_KEY, firstTouch);
  }

  if (!lastTouch || touch.paid || !lastTouch.paid) {
    lastTouch = touch;
    writeTouch(LAST_TOUCH_KEY, lastTouch);
  }

  return { visitorId, firstTouch, lastTouch, newTouch: touch };
}

/** Contexto de atribución para adjuntar a los payloads hacia el backend. */
export function getAttributionContext(now = new Date()) {
  const firstTouch = readTouch(FIRST_TOUCH_KEY);
  const lastTouch = readTouch(LAST_TOUCH_KEY);
  return {
    visitorId: getOrCreateVisitorId(),
    firstTouch: firstTouch && !isExpired(firstTouch, now) ? firstTouch : null,
    lastTouch: lastTouch && !isExpired(lastTouch, now) ? lastTouch : null,
  };
}
